import { SEOContentFAQ } from "../../../../components/seo/SEOContentFAQ";
import { SEOContentHowTo } from "../../../../components/seo/SEOContentHowTo";

export const TeleprompterCapCutPT = () => (
    <>
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Teleprompter para CapCut: Grave Conteúdo Profissional em Minutos
        </h2>

        <p className="lead text-xl text-slate-300 mb-8 font-medium">
            Domine a arte de gravar vídeos impecáveis para o CapCut. <strong>Elimine erros de gravação e mantenha o contato visual perfeito</strong> com o teleprompter inteligente do PromptNinja.
        </p>

        <p className="text-slate-300 mb-8">
            Neste guia essencial para criadores, mostramos por que o PromptNinja é o parceiro indispensável para quem edita no CapCut. Aprenda como gravar um material bruto de alta qualidade logo na primeira take, economizando horas preciosas de edição e permitindo que você foque no que realmente importa: a criatividade e os efeitos virais do CapCut. Descubra como nossa ferramenta online e gratuita transforma seu smartphone ou computador em um estúdio profissional, garantindo fluidez, confiança e autoridade em cada vídeo.
        </p>

        <div className="bg-slate-900/50 p-6 md:p-8 rounded-2xl border border-slate-800 shadow-xl mb-12">
            <h3 className="text-2xl font-bold text-white mb-6">Por que Usar o PromptNinja Antes de Editar no CapCut?</h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <li className="flex items-start gap-3 bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                    <span className="text-blue-400 text-xl">💎</span>
                    <div className="text-sm">
                        <strong className="text-white block mb-1">Material Bruto de Qualidade</strong>
                        <span className="text-slate-400">Grave vídeos fluidos e sem erros. Isso facilita enormemente a aplicação de efeitos e transições no CapCut.</span>
                    </div>
                </li>
                <li className="flex items-start gap-3 bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                    <span className="text-blue-400 text-xl">🎨</span>
                    <div className="text-sm">
                        <strong className="text-white block mb-1">Foco na Edição Criativa</strong>
                        <span className="text-slate-400">Em vez de gastar tempo cortando gaguejos, use seu tempo no CapCut para adicionar legendas e efeitos incríveis.</span>
                    </div>
                </li>
                <li className="flex items-start gap-3 bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                    <span className="text-blue-400 text-xl">👁️</span>
                    <div className="text-sm">
                        <strong className="text-white block mb-1">Contato Visual Mantido</strong>
                        <span className="text-slate-400">O teleprompter permite que você olhe diretamente para a lente enquanto lê, tornando seu vídeo muito mais profissional.</span>
                    </div>
                </li>
                <li className="flex items-start gap-3 bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                    <span className="text-blue-400 text-xl">⚡</span>
                    <div className="text-sm">
                        <strong className="text-white block mb-1">Agilidade na Produção</strong>
                        <span className="text-slate-400">Grave mais rápido, edite mais rápido e publique com muito mais frequência. O PromptNinja acelera todo o seu processo.</span>
                    </div>
                </li>
            </ul>
        </div>

        <section id="capcut-vs-promptninja" className="p-8 bg-slate-900/40 rounded-2xl my-12 border border-slate-800 shadow-2xl relative overflow-hidden text-center">
            <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
                <div className="absolute top-[-10%] right-[-10%] w-64 h-64 bg-cyan-500 rounded-full blur-3xl"></div>
                <div className="absolute bottom-[-10%] left-[-10%] w-64 h-64 bg-indigo-500 rounded-full blur-3xl"></div>
            </div>

            <h2 className="text-3xl font-extrabold text-white mb-8 relative z-10">CapCut vs. PromptNinja: Onde Cada Um Brilha?</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative z-10">
                <div className="p-6 bg-slate-800 rounded-xl shadow-md border-l-4 border-cyan-400 hover:translate-y-[-5px] transition-all">
                    <h3 className="text-2xl font-bold text-cyan-400 mb-6 flex items-center justify-center gap-2">
                        <span>✂️</span> CapCut (Edição)
                    </h3>
                    <ul className="text-slate-400 space-y-4 text-left text-sm">
                        <li className="flex items-center gap-2"><span className="text-cyan-500">✔</span> Legendas automáticas dinâmicas</li>
                        <li className="flex items-center gap-2"><span className="text-cyan-500">✔</span> Filtros e efeitos de vídeo virais</li>
                        <li className="flex items-center gap-2"><span className="text-cyan-500">✔</span> Biblioteca imensa de músicas e sons</li>
                        <li className="flex items-center gap-2"><span className="text-cyan-500">✔</span> Corte e montagem intuitivos</li>
                    </ul>
                </div>
                <div className="p-6 bg-slate-800 rounded-xl shadow-md border-l-4 border-indigo-400 hover:translate-y-[-5px] transition-all">
                    <h3 className="text-2xl font-bold text-indigo-400 mb-6 flex items-center justify-center gap-2">
                        <span>🎤</span> PromptNinja (Gravação)
                    </h3>
                    <ul className="text-slate-400 space-y-4 text-left text-sm">
                        <li className="flex items-center gap-2"><span className="text-indigo-500">✔</span> Teleprompter online 100% gratuito</li>
                        <li className="flex items-center gap-2"><span className="text-indigo-500">✔</span> Controle de voz inteligente (IA)</li>
                        <li className="flex items-center gap-2"><span className="text-indigo-500">✔</span> Controle remoto via smartphone</li>
                        <li className="flex items-center gap-2"><span className="text-indigo-500">✔</span> Redução de erros de gravação em 80%</li>
                    </ul>
                </div>
            </div>

            <div className="mt-10 relative z-10">
                <p className="text-cyan-200 font-semibold text-lg italic mb-8">"Use o PromptNinja para gravar o material perfeito e o CapCut para dar o toque final de mestre!"</p>
                <a href="https://promptninja.solutionkit.com.br/#app" className="inline-block bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-4 px-12 rounded-full shadow-xl shadow-cyan-500/20 transition-all transform hover:scale-105">
                    Começar a Gravar Agora
                </a>
            </div>
        </section>

        <div className="bg-gradient-to-r from-red-900/30 to-orange-900/30 p-8 rounded-xl border border-red-500/30 my-12">
            <h3 className="text-3xl font-bold text-white mb-6">O Ciclo Vicioso do Criador de Conteúdo</h3>
            <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-slate-900/50 p-6 rounded-lg border border-red-500/30">
                    <h3 className="font-bold text-red-400 mb-4 flex items-center gap-2">
                        <span>❌</span> SEM Teleprompter
                    </h3>
                    <ul className="space-y-3 text-slate-300 text-sm">
                        <li>🎬 <strong>Gravação:</strong> 2h pra gravar 3min de vídeo (20+ takes)</li>
                        <li>📂 <strong>Material bruto:</strong> 18min de arquivo cheio de erro</li>
                        <li>✂️ <strong>Edição no CapCut:</strong> 3h cortando falhas, sincronizando cortes</li>
                        <li>😫 <strong>Criatividade:</strong> Zero. Só quer terminar logo</li>
                        <li>📊 <strong>Resultado:</strong> Vídeo "ok", publicado sem capricho</li>
                        <li>⏰ <strong>Tempo total:</strong> 5h+ por vídeo</li>
                    </ul>
                </div>
                <div className="bg-slate-900/50 p-6 rounded-lg border border-green-500/30">
                    <h3 className="font-bold text-green-400 mb-4 flex items-center gap-2">
                        <span>✅</span> COM PromptNinja
                    </h3>
                    <ul className="space-y-3 text-slate-300 text-sm">
                        <li>🎬 <strong>Gravação:</strong> 20min pra gravar 3min (1-2 takes)</li>
                        <li>📂 <strong>Material bruto:</strong> 4min de arquivo limpo</li>
                        <li>✂️ <strong>Edição no CapCut:</strong> 1h criando efeitos, legendas, transições</li>
                        <li>😊 <strong>Criatividade:</strong> Alta. Foca no que importa</li>
                        <li>📊 <strong>Resultado:</strong> Vídeo profissional, com sua marca</li>
                        <li>⏰ <strong>Tempo total:</strong> 1h30min por vídeo</li>
                    </ul>
                </div>
            </div>
            <p className="text-slate-400 text-sm mt-6 italic text-center">
                💡 Economia: <strong className="text-green-400">3h40min por vídeo</strong>. Se você faz 4 vídeos/mês, são <strong className="text-green-400">14h economizadas</strong>!
            </p>
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">Por Que o Teleprompter Embutido do CapCut Não Resolve</h3>
        <p className="mb-6">
            O CapCut tem um teleprompter nativo na função de gravação. Parece prático, mas ele tem limitações críticas que impedem gravações profissionais:
        </p>

        <div className="overflow-x-auto my-8">
            <table className="w-full text-left border-collapse bg-slate-800 rounded-lg">
                <thead>
                    <tr className="bg-slate-700">
                        <th className="p-4 border border-slate-600">Recurso</th>
                        <th className="p-4 border border-slate-600 text-center">PromptNinja</th>
                        <th className="p-4 border border-slate-600 text-center">Teleprompter CapCut</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className="p-4 border border-slate-700 font-semibold">Controle Remoto</td>
                        <td className="p-4 border border-slate-700 text-center text-green-400">✔ Via QR Code (outro celular)</td>
                        <td className="p-4 border border-slate-700 text-center text-red-400">✖ Toque na tela (impossível durante gravação)</td>
                    </tr>
                    <tr>
                        <td className="p-4 border border-slate-700 font-semibold">Velocidade Ajustável em Tempo Real</td>
                        <td className="p-4 border border-slate-700 text-center text-green-400">✔ Ajuste instantâneo pelo controle</td>
                        <td className="p-4 border border-slate-700 text-center text-yellow-400">~ Precisa pausar gravação</td>
                    </tr>
                    <tr>
                        <td className="p-4 border border-slate-700 font-semibold">Controle por Voz (IA)</td>
                        <td className="p-4 border border-slate-700 text-center text-green-400">✔ Texto rola conforme você fala</td>
                        <td className="p-4 border border-slate-700 text-center text-red-400">✖ Não disponível</td>
                    </tr>
                    <tr>
                        <td className="p-4 border border-slate-700 font-semibold">Funciona em Qualquer App de Câmera</td>
                        <td className="p-4 border border-slate-700 text-center text-green-400">✔ Roda separado (overlay)</td>
                        <td className="p-4 border border-slate-700 text-center text-red-400">✖ Só dentro do CapCut</td>
                    </tr>
                    <tr>
                        <td className="p-4 border border-slate-700 font-semibold">Tamanho de Fonte</td>
                        <td className="p-4 border border-slate-700 text-center text-green-400">Personalizável (8px-200px)</td>
                        <td className="p-4 border border-slate-700 text-center text-yellow-400">Limitado (3 tamanhos fixos)</td>
                    </tr>
                    <tr>
                        <td className="p-4 border border-slate-700 font-semibold">Funciona Offline</td>
                        <td className="p-4 border border-slate-700 text-center text-green-400">✔ PWA instalável</td>
                        <td className="p-4 border border-slate-700 text-center text-yellow-400">~ Depende do CapCut estar atualizado</td>
                    </tr>
                    <tr>
                        <td className="p-4 border border-slate-700 font-semibold">Preço</td>
                        <td className="p-4 border border-slate-700 text-center text-green-400">Grátis (controle remoto incluído)</td>
                        <td className="p-4 border border-slate-700 text-center text-green-400">Grátis</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <p className="text-slate-400 text-sm italic mb-12">
            💡 <strong>Dica Pro:</strong> Use o PromptNinja pra gravar, mas aproveite o app de câmera nativo do celular em 4K (60fps). Depois, importa pro CapCut com a melhor qualidade possível.
        </p>

        <div className="bg-slate-800 p-6 rounded-lg my-12 border-l-4 border-cyan-500">
            <h3 className="text-2xl font-bold text-white mb-4">Workflow Profissional: PromptNinja → CapCut</h3>
            <p className="text-slate-300 mb-4">
                A combinação perfeita não é escolher um ou outro. É usar cada ferramenta pro que ela faz de melhor:
            </p>
            <ul className="space-y-3 text-slate-300">
                <li><strong className="text-cyan-400">PromptNinja:</strong> Gravar material 100% limpo, sem erros de texto</li>
                <li><strong className="text-cyan-400">CapCut:</strong> Adicionar legendas automáticas, transições virais, efeitos trendings, música</li>
            </ul>
        </div>

        <SEOContentHowTo
            title="Passo a Passo: Gravação Perfeita para CapCut"
            schemaTitle="Como Usar Teleprompter com CapCut"
            totalTime="PT15M"
            tools={["PromptNinja", "Smartphone", "CapCut"]}
            steps={[
                {
                    title: "Passo 1: Escrever o Roteiro no PromptNinja",
                    text: "Acesse promptninja.solutionkit.com.br e cole seu texto. Ajuste o tamanho da fonte pra ler confortavelmente a 1 metro de distância. Use CAPS ou cores pra marcar palavras-chave que você quer enfatizar na fala."
                },
                {
                    title: "Passo 2: Configurar Controle Remoto (Opcional mas Recomendado)",
                    text: "Clique em 'Conectar Dispositivo' e escaneie o QR Code com um segundo celular ou tablet. Isso permite pausar/retomar/ajustar velocidade sem tocar no celular que está gravando."
                },
                {
                    title: "Passo 3: Posicionar o Celular e o Teleprompter",
                    text: "Coloque o celular que vai gravar em um tripé na altura dos olhos. Abra o PromptNinja em outro dispositivo (ou no mesmo, usando modo picture-in-picture se disponível) e posicione logo abaixo ou ao lado da câmera."
                },
                {
                    title: "Passo 4: Gravar com Foco Total",
                    text: "Aperte REC no app de câmera nativo (não no CapCut ainda). Leia o texto naturalmente, pausando quando necessário usando o controle remoto. Sem pressa. Uma take perfeita vale mais que 10 médias."
                },
                {
                    title: "Passo 5: Importar pro CapCut",
                    text: "Abra o CapCut, crie um novo projeto e importe o vídeo da galeria. Como você não errou o texto, sua timeline vai estar limpa. Agora é só adicionar a mágica: Auto Captions, efeitos de zoom, música de fundo."
                },
                {
                    title: "Passo 6: Legendas Automáticas de Alta Precisão",
                    text: "Use 'Texto → Auto Caption'. Como sua dicção foi guiada pelo roteiro, o CapCut vai gerar legendas com 95%+ de precisão. Corrija as poucas palavras erradas e aplique animações virais (bounce, glitch, etc)."
                }
            ]}
        />

        <h3 className="text-3xl font-bold text-white mt-16 mb-6">Caso Real: TikToker Passou de 8h para 3h/Semana Gravando</h3>
        <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 p-8 rounded-xl border border-purple-500/30 mb-12">
            <p className="text-slate-300 mb-4">
                <strong className="text-purple-400">Perfil:</strong> Criadora de conteúdo educacional no TikTok (@educar_com_amor), 87k seguidores, posta 5 vídeos/semana sobre dicas de estudo.
            </p>
            <p className="text-slate-300 mb-6">
                <strong className="text-purple-400">Problema:</strong> Gastava 8h toda segunda-feira gravando os 5 vídeos da semana. Como decorava o texto, travava muito, e cada vídeo de 60s levava 1h30min pra ficar "aceitável". Resultado: burnout, vídeos sem energia nos últimos takes.
            </p>

            <h3 className="font-bold text-white mb-3">Mudanças Implementadas:</h3>
            <ol className="list-decimal pl-6 space-y-2 text-slate-300 mb-6">
                <li>Começou a escrever roteiros detalhados no domingo (30min pra 5 vídeos)</li>
                <li>Segunda-feira: abre PromptNinja no tablet, coloca atrás do celular</li>
                <li>Grava os 5 vídeos em 2h (cada um leva 20-25min, 2 takes no máximo)</li>
                <li>Edita no CapCut: 1h total (12min por vídeo, focando em legendas e efeitos)</li>
            </ol>

            <div className="bg-slate-900/50 p-6 rounded-lg">
                <h3 className="font-bold text-green-400 mb-3">Resultados em 2 Meses:</h3>
                <ul className="space-y-2 text-slate-300 text-sm">
                    <li>⏱️ Tempo de produção: <strong>-62%</strong> (de 8h pra 3h/semana)</li>
                    <li>📈 Engajamento médio: <strong>+34%</strong> (vídeos mais fluidos, menos erros visíveis)</li>
                    <li>💪 Energia mental: Sobra criatividade pra testar novos formatos</li>
                    <li>🎬 Qualidade: Vídeos parecem "mais profissionais" segundo comentários</li>
                </ul>
            </div>

            <p className="text-slate-400 text-xs italic mt-4">
                *Dados compartilhados com permissão. Resultados podem variar conforme tipo de conteúdo e experiência.
            </p>
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">Dicas Avançadas para Usuario CapCut + PromptNinja</h3>

        <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
                <h3 className="font-bold text-cyan-400 mb-3 text-xl">📱 Grave em Portrait 9:16</h3>
                <p className="text-slate-300 text-sm mb-3">
                    Se seu conteúdo é pra TikTok/Reels/Shorts, configure o PromptNinja em modo retrato (vertical). Ajuste a largura do texto pra caber na tela sem rolar horizontalmente.
                </p>
                <p className="text-xs text-slate-500">
                    <strong>Pro tip:</strong> Use o celular na horizontal só se for YouTube widescreen.
                </p>
            </div>

            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
                <h3 className="font-bold text-cyan-400 mb-3 text-xl">🎨 Marque Pontos de Corte no Roteiro</h3>
                <p className="text-slate-300 text-sm mb-3">
                    Use [CORTE] ou *** no roteiro pra lembrar onde você vai adicionar transições ou b-roll no CapCut. Isso facilita na hora de editar.
                </p>
                <p className="text-xs text-slate-500">
                    <strong>Exemplo:</strong> "Aqui estão 3 dicas [CORTE] Primeira: sempre planeje..."
                </p>
            </div>

            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
                <h3 className="font-bold text-cyan-400 mb-3 text-xl">⚡ Use o Recurso [COUNT 3]</h3>
                <p className="text-slate-300 text-sm mb-3">
                    No PromptNinja Pro, você pode inserir comandos especiais como [COUNT 3] pra fazer uma contagem regressiva falada. Perfeito pra criar suspense antes de revelar algo.
                </p>
                <p className="text-xs text-slate-500">
                    <strong>CapCut vai amar:</strong> Fácil de sincronizar zoom ou efeito glitch na contagem.
                </p>
            </div>

            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
                <h3 className="font-bold text-cyan-400 mb-3 text-xl">🎯 Velocidade de 180-200 WPM</h3>
                <p className="text-slate-300 text-sm mb-3">
                    Para vídeos curtos (TikTok/Shorts), configure velocidade entre 180-200 palavras por minuto. É rápido mas ainda compreensível. Para YouTube longo, use 140-160 WPM.
                </p>
                <p className="text-xs text-slate-500">
                    <strong>Teste:</strong> Grave 30s e veja se conseguiu acompanhar confortavelmente.
                </p>
            </div>
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">Erros Comuns ao Combinar Teleprompter + CapCut</h3>

        <div className="space-y-4 mb-12">
            <div className="bg-slate-800 p-5 rounded-lg border-l-4 border-red-500">
                <h3 className="font-bold text-white mb-2">❌ Erro 1: Gravar Direto no CapCut</h3>
                <p className="text-slate-300 text-sm mb-2">
                    Muita gente grava usando a câmera do CapCut porque já vai estar lá mesmo. O problema: se o app travar ou você fechar sem querer, perde a gravação.
                </p>
                <p className="text-green-400 text-sm">
                    <strong>✅ Solução:</strong> Sempre grave no app de câmera nativo do celular. Salva automaticamente na galeria e você tem backup.
                </p>
            </div>

            <div className="bg-slate-800 p-5 rounded-lg border-l-4 border-red-500">
                <h3 className="font-bold text-white mb-2">❌ Erro 2: Não Testar o Posicionamento Antes</h3>
                <p className="text-slate-300 text-sm mb-2">
                    Posicionar o teleprompter muito longe da câmera faz você desviar MUITO o olhar, ficando óbvio que está lendo.
                </p>
                <p className="text-green-400 text-sm">
                    <strong>✅ Solução:</strong> Coloque o dispositivo com PromptNinja <em>logo abaixo ou ao lado</em> da lente. O ideal é no máximo 20cm de distância.
                </p>
            </div>

            <div className="bg-slate-800 p-5 rounded-lg border-l-4 border-red-500">
                <h3 className="font-bold text-white mb-2">❌ Erro 3: Fonte Muito Pequena</h3>
                <p className="text-slate-300 text-sm mb-2">
                    Usar fonte 18px quando você tá a 1 metro de distância força você a franzir os olhos ou se aproximar, quebrando a naturalidade.
                </p>
                <p className="text-green-400 text-sm">
                    <strong>✅ Solução:</strong> Use pelo menos 32-40px. Teste: você deve conseguir ler SEM esforço. Ajuste conforme distância e qualidade da sua visão.
                </p>
            </div>

            <div className="bg-slate-800 p-5 rounded-lg border-l-4 border-red-500">
                <h3 className="font-bold text-white mb-2">❌ Erro 4: Editar Antes de Gravar Tudo</h3>
                <p className="text-slate-300 text-sm mb-2">
                    Gravar um vídeo, já ir editar, depois gravar outro. Você perde o ritmo e cada gravação fica com energia diferente.
                </p>
                <p className="text-green-400 text-sm">
                    <strong>✅ Solução:</strong> Método batch: grave TODOS os vídeos da semana de uma vez (com setup igual). Depois edita todos de uma vez no CapCut.
                </p>
            </div>
        </div>

        <div className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 p-8 rounded-xl border border-cyan-500/30 my-12">
            <h3 className="text-2xl font-bold text-white mb-4">🔥 Hack de Produtividade: Gravar em Lote</h3>
            <p className="text-slate-300 mb-4">
                Se você precisa produzir múltiplos vídeos, configure o ambiente UMA vez só e grave tudo em sequência:
            </p>
            <ol className="list-decimal pl-6 space-y-2 text-slate-300 mb-4">
                <li>Prepare 5 roteiros no PromptNinja (cole todos em abas separadas do navegador)</li>
                <li>Configure iluminação e tripé</li>
                <li>Grave vídeo 1, pausa 30 segundos, troca aba, grave vídeo 2...</li>
                <li>Em 1 hora você grava material pra semana inteira</li>
                <li>Dedica 1 dia só pra editar tudo no CapCut</li>
            </ol>
            <p className="text-cyan-300 font-semibold">
                Resultado: Você "trabalha" 2 dias mas libera a semana toda pra outras tarefas (thumbnails, copy, análise).
            </p>
        </div>

        <div className="text-center mt-12 mb-12">
            <a
                href="https://promptninja.solutionkit.com.br/#app"
                className="inline-block bg-gradient-to-r from-cyan-500 to-blue-600 font-bold py-4 px-10 rounded-full transition hover:brightness-110 hover:scale-105 transform shadow-lg"
                style={{ color: 'white' }}
            >
                Comece a Gravar Conteúdo Profissional Agora (Grátis)
            </a>
            <p className="text-slate-400 mt-4 text-sm">Sem cadastro. Sem cartão. Sem pegadinhas.</p>
        </div>

        <SEOContentFAQ
            title="Perguntas Frequentes: PromptNinja + CapCut"
            items={[
                {
                    question: "O PromptNinja exporta direto para o CapCut?",
                    answer: "Não diretamente. O workflow é: você grava o vídeo usando o app de câmera nativo do celular enquanto lê o teleprompter. O vídeo salva na galeria automaticamente. Depois você abre o CapCut e importa da galeria. Simples e seguro."
                },
                {
                    question: "Posso usar os efeitos e transições do CapCut normalmente?",
                    answer: "Sim! A ideia é justamente essa: gravar o vídeo 'limpo' usando o PromptNinja (sem erros de texto), e depois adicionar toda a mágica no CapCut — legendas automáticas, zoom dramático, música viral, transições trendings. Cada ferramenta faz o que faz de melhor."
                },
                {
                    question: "A qualidade do vídeo fica boa?",
                    answer: "A qualidade depende da câmera do seu celular, não do PromptNinja. Como você grava usando o app de câmera nativo (e não um gravador dentro do navegador), consegue usar a resolução máxima: 4K 60fps se o celular suportar. O PromptNinja só mostra o texto, não toca na gravação."
                },
                {
                    question: "Preciso de internet pra usar?",
                    answer: "Pra primeira vez: sim, pra carregar o site. Depois você pode instalar como PWA (atalho na tela inicial) e funciona 100% offline. O controle remoto P2P também não precisa de internet — só Wi-Fi local pra conectar os dois dispositivos."
                },
                {
                    question: "E se eu quiser gravar um podcast longo de 30-40 minutos?",
                    answer: "Perfeito! Use o recurso de 'Scroll Automático' com velocidade ajustável, ou melhor ainda: o Controle por Voz (Pro). O texto anda conforme você fala, sem precisar tocar em nada. Ideal pra conteúdo longo onde você precisa gesticular e se mover."
                },
                {
                    question: "Funciona em iPhone e Android?",
                    answer: "Sim, ambos. O PromptNinja roda 100% no navegador (Safari no iPhone, Chrome no Android). Não precisa baixar nada na App Store ou Google Play. É só acessar o site e usar. Pode inclusive instalar como app na tela inicial (PWA)."
                },
                {
                    question: "Tem limite de tempo de gravação?",
                    answer: "O PromptNinja não limita. Ele só mostra texto. O limite é do seu celular: espaço de armazenamento e bateria. Dica: se vai gravar muito (20min+), deixa o celular no carregador."
                },
                {
                    question: "As legendas automáticas do CapCut ficam melhores com teleprompter?",
                    answer: "SIM! Muito melhores. Como você fala seguindo um roteiro escrito, sua dicção fica mais clara e sem gaguejos. O algoritmo de Auto Caption do CapCut consegue reconhecer as palavras com 90-95% de precisão (vs ~70% quando você improvisa). Menos correção manual = mais tempo pra criatividade."
                }
            ]}
        />

        <div className="bg-slate-800 p-8 rounded-lg mt-16 mb-8">
            <h3 className="text-2xl font-bold text-white mb-4 text-center">Recursos Relacionados</h3>
            <div className="grid md:grid-cols-2 gap-4">
                <a href="/teleprompter-tiktok-shorts" className="block p-4 bg-slate-900 rounded-lg hover:bg-slate-700 transition border border-slate-700">
                    <span className="text-2xl mb-2 block">📱</span>
                    <h4 className="font-bold text-white mb-1">Teleprompter para TikTok e Shorts</h4>
                    <p className="text-sm text-slate-400">Dicas específicas para vídeos verticais curtos</p>
                </a>
                <a href="/teleprompter-para-youtubers-e-criadores" className="block p-4 bg-slate-900 rounded-lg hover:bg-slate-700 transition border border-slate-700">
                    <span className="text-2xl mb-2 block">🎬</span>
                    <h4 className="font-bold text-white mb-1">Teleprompter para YouTubers</h4>
                    <p className="text-sm text-slate-400">Como duplicar sua produtividade de vídeos</p>
                </a>
                <a href="/como-usar-teleprompter-celular" className="block p-4 bg-slate-900 rounded-lg hover:bg-slate-700 transition border border-slate-700">
                    <span className="text-2xl mb-2 block">📖</span>
                    <h4 className="font-bold text-white mb-1">Guia Completo de Uso</h4>
                    <p className="text-sm text-slate-400">Tutorial passo a passo para iniciantes</p>
                </a>
                <a href="/melhor-teleprompter-app" className="block p-4 bg-slate-900 rounded-lg hover:bg-slate-700 transition border border-slate-700">
                    <span className="text-2xl mb-2 block">⭐</span>
                    <h4 className="font-bold text-white mb-1">Comparativo de Apps</h4>
                    <p className="text-sm text-slate-400">PromptNinja vs concorrentes pagos</p>
                </a>
                <a href="/scripts-teleprompter" className="block p-4 bg-slate-900 rounded-lg hover:bg-slate-700 transition border border-slate-700">
                    <span className="text-2xl mb-2 block">📝</span>
                    <h4 className="font-bold text-white mb-1">Modelos de Roteiro Prontos</h4>
                    <p className="text-sm text-slate-400">Scripts gratuitos para você adaptar</p>
                </a>
                <a href="/dicas-oratoria-video" className="block p-4 bg-slate-900 rounded-lg hover:bg-slate-700 transition border border-slate-700">
                    <span className="text-2xl mb-2 block">🎤</span>
                    <h4 className="font-bold text-white mb-1">Dicas de Oratória em Vídeo</h4>
                    <p className="text-sm text-slate-400">Como falar naturalmente lendo teleprompter</p>
                </a>
            </div>
        </div>

        <div className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 p-8 rounded-xl border border-purple-500/30 text-center my-12">
            <h3 className="text-3xl font-bold text-white mb-4">Sua Jornada de Criador Começa Aqui</h3>
            <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                Pare de brigar com regravações. Pare de passar 3 horas editando erros. Comece a criar conteúdo do jeito que sempre sonhou: com foco, qualidade e criatividade de sobra.
            </p>
            <a
                href="https://promptninja.solutionkit.com.br/#app"
                className="inline-block bg-gradient-to-r from-purple-500 to-pink-500 font-bold py-4 px-10 rounded-full transition hover:brightness-110 hover:scale-105 transform shadow-lg text-lg"
                style={{ color: 'white' }}
            >
                Usar PromptNinja Grátis Agora →
            </a>
        </div>
    </>
);
